import _defineProperty from "@babel/runtime/helpers/defineProperty";
import React from 'react';
import { isInternetExplorer } from '../../common/utils/browser';
import { memoize, isNumber, orderBy } from 'lodash';
import { getResizedImageUrl } from '../../common/utils/get-resized-image-url';
import classnames from 'classnames';
import styles from './picture.scss';
import { PictureMode } from './picture.types';
const memoizeMap = func => {
  const memoizedFunc = memoize(func, _ref => {
    let {
      url,
      min,
      max,
      width,
      height
    } = _ref;
    return [url, min, max, width, height].join('-');
  });
  memoizedFunc.cache = new Map();
  return memoizedFunc;
};
export class Picture extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "getSrc", (url, width, height) => {
      if (!url) {
        return '';
      }
      const devicePixelRatio = typeof window !== 'undefined' ? window.devicePixelRatio ?? 1 : 1;
      const defaultRatios = [1, 2, 3];
      const pixelRatios = Array.from(new Set([...defaultRatios, devicePixelRatio]));
      return pixelRatios.map(ratio => `${getResizedImageUrl({
        url,
        width: width * ratio,
        height: height * ratio
      })} ${ratio}x`).join(', ');
    });
    _defineProperty(this, "getBreakpointSource", memoizeMap(_ref2 => {
      let {
        url,
        min,
        max,
        width,
        height
      } = _ref2;
      const src = this.getSrc(url, width, height);
      let media = '';
      if (isNumber(min)) {
        media += `(min-width: ${min}px)`;
      }
      if (isNumber(max)) {
        if (media) {
          media += ' and ';
        }
        media += `(max-width: ${max}px)`;
      }
      return {
        media,
        src
      };
    }));
    this.state = {
      isIE: false,
      isClient: false
    };
  }
  componentDidMount() {
    this.setState({
      isIE: isInternetExplorer(),
      isClient: true
    });
  }
  getDefaultBackgroundStyle(_ref3) {
    let {
      fallbackToBackgroundImage,
      sources
    } = _ref3;
    const defaultSrc = fallbackToBackgroundImage ? sources[sources.length - 1].src : sources[0].src;
    const imageSet = defaultSrc.split(', ').map(src => {
      const match = src.match(/^(.+?)(?: (\d(?:\.\d+)?)x)?$/);
      if (!match) {
        return `url("${src}")`;
      }
      const [, url, ratio = '1'] = match;
      return `url("${url}") ${ratio}x`;
    }).join(', ');
    const style = {
      backgroundImage: defaultSrc.includes(', ') ? `-webkit-image-set(${imageSet})` : `url("${defaultSrc}")`
    };
    return {
      style,
      defaultSrc
    };
  }
  render() {
    const {
      src,
      breakpoints,
      dataHook,
      children,
      className,
      alt,
      mode,
      withFallbackColor
    } = this.props;
    const {
      isIE,
      isClient
    } = this.state;
    const orderedBreakpoints = orderBy(breakpoints, _ref4 => {
      let {
        width,
        height
      } = _ref4;
      return width * height;
    });
    const sources = orderedBreakpoints.map(breakpoint => this.getBreakpointSource({
      url: src,
      ...breakpoint
    }));

    // IE does not support object-fit css property, so we fallback to backgroundImage.
    // But we still render img tag for SEO
    const fallbackToBackgroundImage = isIE && mode === PictureMode.COVER;
    const {
      style,
      defaultSrc
    } = this.getDefaultBackgroundStyle({
      fallbackToBackgroundImage,
      sources
    });
    return /*#__PURE__*/React.createElement("div", {
      className: classnames(styles.pictureContainer, className, {
        [styles.contain]: mode === PictureMode.CONTAIN,
        [styles.cover]: mode === PictureMode.COVER,
        [styles.fitToWidth]: mode === PictureMode.FIT_TO_WIDTH,
        [styles.fitToHeight]: mode === PictureMode.FIT_TO_HEIGHT,
        [styles.withFallbackColor]: withFallbackColor
      }),
      "data-hook": dataHook,
      style: style // User first see this image, rendered by SSR and then picture tag appears with relevant breakpoints
    }, /*#__PURE__*/React.createElement("picture", {
      className: styles.picture,
      key: isClient ? 'client' : undefined
    }, sources.filter(source => source.src).map(source => /*#__PURE__*/React.createElement("source", {
      media: source.media,
      srcSet: source.src,
      key: source.src
    })), defaultSrc && /*#__PURE__*/React.createElement("img", {
      alt: alt,
      className: styles.img,
      src: defaultSrc
    })), fallbackToBackgroundImage && /*#__PURE__*/React.createElement("div", {
      className: styles.backgroundImage,
      style: style,
      "data-hook": "picture-background-image"
    }), /*#__PURE__*/React.createElement("div", {
      className: styles.content
    }, children));
  }
}
_defineProperty(Picture, "defaultProps", {
  mode: PictureMode.COVER,
  breakpoints: [{
    min: 0,
    width: 320,
    height: 180
  }]
});
//# sourceMappingURL=picture.js.map